/** EmptyState / ErrorState (DESIGN.md §12, Primer blank-slate anatomy) and StatusBadge. */
import { AlertTriangleIcon, InboxIcon, RotateCwIcon, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Badge } from '../components/badge';
import { Button } from '../components/button';
import { cn } from '../lib/utils';
import { useUiStrings } from '../strings';

export interface EmptyStateProps {
  icon?: LucideIcon;
  title: string;
  description?: string;
  /** Primary action, e.g. "Add user" or "Clear filters". */
  action?: ReactNode;
  className?: string;
}

export function EmptyState({ icon: Icon = InboxIcon, title, description, action, className }: EmptyStateProps) {
  return (
    <div className={cn('flex flex-col items-center justify-center gap-2 px-4 py-12 text-center', className)}>
      <div className="mb-1 flex size-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <Icon className="size-5" />
      </div>
      <p className="text-sm font-medium">{title}</p>
      {description && <p className="max-w-sm text-sm text-muted-foreground">{description}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export interface ErrorStateProps {
  title?: string;
  description?: string;
  onRetry?: () => void;
  className?: string;
}

/** Failed page/pane load with retry (DESIGN.md §5 error pattern). */
export function ErrorState({ title, description, onRetry, className }: ErrorStateProps) {
  const strings = useUiStrings();
  return (
    <div role="alert" className={className}>
      <EmptyState
        icon={AlertTriangleIcon}
        title={title ?? strings.errorTitle}
        description={description ?? strings.errorDescription}
        action={
          onRetry && (
            <Button variant="outline" onClick={onRetry}>
              <RotateCwIcon />
              {strings.retry}
            </Button>
          )
        }
      />
    </div>
  );
}

export type Status = 'ok' | 'warning' | 'error' | 'unknown' | 'inactive';

const statusVariant = {
  ok: 'success',
  warning: 'warning',
  error: 'destructive',
  unknown: 'muted',
  inactive: 'muted',
} as const;

const statusDot = {
  ok: 'bg-success',
  warning: 'bg-warning',
  error: 'bg-destructive',
  unknown: 'bg-muted-foreground',
  inactive: 'bg-muted-foreground',
} as const;

/** Semantic status pill used in lists, cards and headers. */
export function StatusBadge({ status, label }: { status: Status; label: string }) {
  return (
    <Badge variant={statusVariant[status]}>
      <span aria-hidden className={cn('size-1.5 rounded-full', statusDot[status])} />
      {label}
    </Badge>
  );
}
